import { Alert, Badge, Spinner } from '@ashniva/ui';
import type { ReactNode } from 'react';

import { formatDateTime } from '../../../shared/lib/format';
import { freshnessText, relativeTime } from '../freshness';
import { REGISTRATION_STEPS, type RepositoryState, type RepositoryStateKind } from '../ri-state';
import { STATE_BADGE } from '../state-badges';
import { failureReason } from '../ri-text';
import { useNow } from '../use-now';

export function StateBadge({ kind }: { kind: RepositoryStateKind }) {
  const badge = STATE_BADGE[kind];
  return (
    <Badge tone={badge.tone}>
      <span aria-hidden="true">{badge.icon} </span>
      {badge.label}
    </Badge>
  );
}

function stepStatus(index: number, step: number): 'done' | 'current' | 'todo' {
  if (index < step) return 'done';
  return index === step ? 'current' : 'todo';
}

/** The registration flow as steps, the current one marked (P14-03). */
export function RegistrationSteps({ step }: { step: number }) {
  return (
    <ol className="ri-steps" aria-label="Registration steps">
      {REGISTRATION_STEPS.map((label, index) => {
        const status = stepStatus(index, step);
        return (
          <li
            key={label}
            className={`ri-steps__step ri-steps__step--${status}`}
            aria-current={status === 'current' ? 'step' : undefined}
          >
            <span aria-hidden="true">{status === 'done' ? '✓' : index + 1}</span> {label}
            {status === 'current' ? <span className="sr-only"> (current step)</span> : null}
            {status === 'done' ? <span className="sr-only"> (done)</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

interface RepositoryStateViewProps {
  state: RepositoryState;
  /** Whether the person can request indexing themselves. */
  manage: boolean;
  /** The buttons for this state (request indexing, sync now, …). */
  actions?: ReactNode;
}

/** One repository's state, in full (MASTER-SPEC §14.4): what it is, since when, why, and what to do next. */
export function RepositoryStateView({ state, manage, actions }: RepositoryStateViewProps) {
  const now = useNow();
  const reason = failureReason(state.reasonCode);
  return (
    <div className="ri-state" data-state={state.kind}>
      <div className="ri-state__head">
        <StateBadge kind={state.kind} />
        {state.kind === 'ready' || state.kind === 'stale' ? (
          <span className="ri-state__freshness">
            {freshnessText(state.indexedCommit, state.indexedAt, now)}
          </span>
        ) : null}
      </div>
      <StateBody state={state} manage={manage} now={now} reason={reason} />
      {actions ? <div className="ri-state__actions">{actions}</div> : null}
    </div>
  );
}

function StateBody({
  state,
  manage,
  now,
  reason,
}: {
  state: RepositoryState;
  manage: boolean;
  now: number;
  reason: string | null;
}) {
  switch (state.kind) {
    case 'ready':
      return state.syncing ? <p className="muted">Updating from the code host now.</p> : null;
    case 'stale':
      return (
        <Alert tone="warning" title="Results may be outdated">
          Updates are failing
          {state.since
            ? ` since ${formatDateTime(state.since)} (${relativeTime(state.since, now)})`
            : ''}
          ; results may be outdated.{reason ? ` ${reason}` : ''}
        </Alert>
      );
    case 'indexing':
      return (
        <>
          <p className="ri-state__line">
            <Spinner size="sm" decorative /> Indexing the code. How long it takes depends on the
            size of the repository.
          </p>
          {state.since ? <p className="muted">Started at {formatDateTime(state.since)}.</p> : null}
          <RegistrationSteps step={state.step ?? 3} />
        </>
      );
    case 'pending':
      return (
        <>
          <p>
            Indexing was requested
            {state.since ? ` on ${formatDateTime(state.since)}` : ''}. A Repository Intelligence
            operator approves each repository before it is copied.
          </p>
          <RegistrationSteps step={state.step ?? 0} />
        </>
      );
    case 'provisioning':
      return (
        <>
          <p>The repository is being copied and checked before it is indexed.</p>
          <RegistrationSteps step={state.step ?? 2} />
        </>
      );
    case 'not-registered':
      return (
        <p>
          This repository is not indexed by Repository Intelligence.{' '}
          {manage
            ? 'Request indexing to search it, read it and ask about it here.'
            : 'Ask an administrator to request indexing.'}
        </p>
      );
    case 'rejected':
      return (
        <>
          <p>The request to index this repository was rejected.</p>
          {state.decisionReason ? (
            <blockquote className="ri-quote">
              <span className="sr-only">The operator wrote: </span>
              {state.decisionReason}
            </blockquote>
          ) : null}
        </>
      );
    case 'suspended':
    case 'failed':
      return (
        <p>
          {state.kind === 'suspended'
            ? 'Repository Intelligence suspended this repository'
            : 'Not available'}
          {state.since ? ` since ${formatDateTime(state.since)}` : ''}.{' '}
          {reason ?? 'No reason was given.'}
        </p>
      );
  }
}
